import { CustomError } from './CustomError'
import { PuError } from './PuError'

/**
 * Safely stringify values that might contain circular references or unsupported types
 */
function safeStringify(value: any): string {
  try {
    return JSON.stringify(value)
  } catch {
    return '[unserializable]'
  }
}

/**
 * Base error class for API fetch operations
 * Provides consistent error messages for Sentry grouping while preserving context data for debugging
 *
 * This class standardizes error handling across all API repositories and eliminates code duplication.
 * Usage: new BaseApiFetchError('calendar-availability', url, params, originalError)
 */
export class BaseApiFetchError extends CustomError implements PuError {
  readonly statusCode: number = ERROR_CODE_API_FETCH

  public readonly endpoint: string
  public readonly url: string
  public readonly params: Record<string, any>
  public readonly httpStatusCode?: number
  public readonly statusText?: string
  public readonly responseData?: any
  public readonly isTimeout: boolean
  public readonly originalError: any

  constructor(
    endpoint: string,
    url: string,
    params: Record<string, any>,
    originalError: any,
  ) {
    // Detect error type for consistent Sentry grouping
    const isTimeout =
      originalError?.message?.includes('timeout') ||
      originalError?.name === 'TimeoutError'
    const httpStatusCode = originalError?.status || originalError?.statusCode

    // Generate consistent message for Sentry grouping (no variable data)
    let message: string
    if (isTimeout) {
      message = `${endpoint} endpoint timeout`
    } else if (httpStatusCode) {
      message = `${endpoint} endpoint failed with status ${httpStatusCode}`
    } else {
      message = `${endpoint} endpoint failed`
    }

    super({
      name: 'BaseApiFetchError',
      message,
      cause: originalError,
    })

    // Store context data (variable data that shouldn't be in the error message)
    this.endpoint = endpoint
    this.url = url
    this.params = params
    this.isTimeout = isTimeout
    this.httpStatusCode = httpStatusCode
    this.statusText = originalError?.statusText
    this.responseData = originalError?.data

    // Store originalError as non-enumerable to prevent SSR serialization issues
    // when this error is passed to Nuxt's error page
    Object.defineProperty(this, 'originalError', {
      value: originalError,
      enumerable: false,
      writable: false,
      configurable: true,
    })
  }

  /**
   * Get context data for Sentry or logging
   * This includes all the variable data that shouldn't be in the error message
   * Enhanced with network diagnostics for debugging "Failed to fetch" scenarios
   */
  getContext(): Record<string, unknown> {
    // Redact sensitive data from params before logging
    const redactSensitiveData = (obj: any): any => {
      if (!obj || typeof obj !== 'object') return obj
      const sensitiveKeys = new Set(['authorization', 'password'])
      const copy: any = Array.isArray(obj) ? obj.slice() : { ...obj }
      Object.keys(copy).forEach((key) => {
        if (sensitiveKeys.has(key.toLowerCase())) {
          copy[key] = '[redacted]'
        }
      })
      return copy
    }

    const safeParams = redactSensitiveData(this.params)

    // Build base context
    const baseContext = {
      endpoint: this.endpoint,
      url: this.url,
      params: safeParams, // Keep object format for tooling compatibility
      paramsJson: safeStringify(safeParams), // Add string format for logging
      isTimeout: this.isTimeout,
      statusCode: this.httpStatusCode,
      statusText: this.statusText,
      responseData: this.responseData
        ? safeStringify(this.responseData)
        : undefined,
      originalErrorMessage: this.originalError?.message,
      originalErrorType:
        this.originalError?.name || this.originalError?.constructor?.name,
      stack: this.stack || 'No stack trace available',
    }

    // Enhanced network diagnostics for "Failed to fetch" scenarios
    const networkContext: Record<string, any> = {}

    // Use feature detection instead of process.client for better portability
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      // Browser network context
      networkContext.navigator = {
        onLine: navigator.onLine,
        userAgent: navigator.userAgent,
        language: navigator.language,
        cookieEnabled: navigator.cookieEnabled,
      }

      // Connection info if available (modern browsers)
      if ('connection' in navigator) {
        const connection = (navigator as any).connection
        networkContext.connection = {
          effectiveType: connection?.effectiveType,
          downlink: connection?.downlink,
          rtt: connection?.rtt,
          saveData: connection?.saveData,
        }
      }

      // Page context (safe to access when window is defined)
      if (typeof document !== 'undefined') {
        networkContext.page = {
          protocol: window.location.protocol,
          host: window.location.host,
          referrer: document.referrer || 'direct',
        }
      }
    }

    // Enhanced error classification for consistent monitoring
    // Broaden detection to catch browser variations and localized messages
    const errorMessage = String(this.originalError?.message || '').toLowerCase()
    const isNetworkError =
      this.originalError?.name === 'TypeError' ||
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('load failed') || // iOS Safari pattern
      errorMessage.includes('networkerror') ||
      errorMessage.includes('network error')
    const possibleCauses: string[] = []

    if (isNetworkError) {
      possibleCauses.push(
        'network_connectivity',
        'dns_resolution',
        'cors_policy',
        'ssl_tls_error',
        'ad_blocker',
        'server_unreachable',
      )
    }

    // Unified error classification for monitoring and alerting
    let errorKind: string
    if (this.isTimeout) {
      errorKind = 'timeout'
    } else if (this.httpStatusCode) {
      errorKind = 'http'
    } else if (isNetworkError) {
      errorKind = 'network'
    } else {
      errorKind = 'unknown'
    }

    return {
      ...baseContext,

      // Enhanced network diagnostics
      isNetworkError,
      possibleCauses,
      errorKind,
      ...networkContext,

      // Request timing context for debugging timeouts vs immediate failures
      requestTiming: this.originalError?.requestTiming || {},

      // Error details for debugging "Failed to fetch" scenarios
      originalErrorName: this.originalError?.name,
      originalErrorStack:
        typeof this.originalError?.stack === 'string'
          ? this.originalError.stack.split('\n')?.[0]
          : undefined,

      // Additional debugging hints for network errors
      debuggingHints: isNetworkError
        ? [
            'Check browser network tab for actual HTTP status',
            'Verify server is accessible from same network',
            'Check for CORS headers if cross-origin request',
            'Test same URL in browser address bar',
            'Check for ad blockers or security software',
            'Verify SSL certificate is valid',
          ]
        : [],
    }
  }
}

export const ERROR_CODE_API_FETCH = 502
